// [5.5] HotelFormStep5Registration.jsx – owner account
import React, { useEffect, useMemo, useState } from "react";

export default function HotelFormStep5Registration({ userData, setUserData, errors = {} }) {
  const [passwordError, setPasswordError] = useState("");
  const [confirmError, setConfirmError] = useState("");
  const [passwordStrength, setPasswordStrength] = useState(0);

  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);

  const setField = (key, value) => setUserData((p) => ({ ...p, [key]: value }));

  const handleChange = (e) => {
    const { name, value } = e.target;
    setField(name, value);
  };

  const handleBlurTrim = (e) => {
    const { name, value } = e.target;
    const trimmed = value.trim();
    if (trimmed !== value) setField(name, trimmed);
  };

  const handleKey = (e) => {
    setCapsLockOn(e.getModifierState && e.getModifierState("CapsLock"));
  };

  // Password evaluation (0–5) + messages
  const evaluatePassword = (password) => {
    if (!password) return { strength: 0, errors: [] };

    const errors = [];
    let score = 0;
    const length = password.length;

    if (length >= 8) score += 1; else errors.push("At least 8 characters");
    if (length >= 12) score += 1;
    if (length >= 16) score += 1;

    if (/[A-Z]/.test(password)) score += 1; else errors.push("Add an uppercase letter");
    if (/[a-z]/.test(password)) score += 1; else errors.push("Add a lowercase letter");
    if (/[0-9]/.test(password)) score += 1; else errors.push("Add a number");
    if (/[^A-Za-z0-9]/.test(password)) score += 1; else errors.push("Add a symbol");

    if (/(password|qwerty|letmein|12345|admin)/i.test(password)) {
      errors.push("Avoid common words/patterns");
      score = Math.max(1, score - 2);
    }

    const strength = Math.min(5, Math.max(0, score));
    return { strength, errors };
  };

  const strengthMeta = useMemo(() => {
    const s = passwordStrength;
    if (s <= 2) return { label: "Weak", color: "#ff4d4f" };
    if (s <= 4) return { label: "Medium", color: "#faad14" };
    return { label: "Strong", color: "#52c41a" };
  }, [passwordStrength]);

  useEffect(() => {
    const { strength, errors: pwErrors } = evaluatePassword(userData.password || "");
    setPasswordStrength(strength);
    setPasswordError(pwErrors.join(", "));

    if ((userData.confirmPassword || "").length > 0) {
      setConfirmError(userData.password === userData.confirmPassword ? "" : "Passwords do not match");
    } else {
      setConfirmError("");
    }
  }, [userData.password, userData.confirmPassword]);

  // Accessibility helper for the <span> toggles
  const spanToggleProps = (isOn, setFn, label) => ({
    role: "switch",
    "aria-label": label,
    "aria-checked": isOn,
    tabIndex: 0,
    onClick: () => setFn(v => !v),
    onKeyDown: (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        setFn(v => !v);
      }
    },
    onMouseDown: (e) => e.preventDefault(), // keep focus in the input
    className: "eye-toggle",
    children: isOn ? "🙈" : "👁️",
  });

  return (
    <div>
      <h2>Account Registration - Step 5</h2>

      <div className="form-group">
        <label>First Name</label>
        <input
          name="firstName"
          value={userData.firstName}
          onChange={handleChange}
          onBlur={handleBlurTrim}
          autoComplete="given-name"
        />
      </div>

      <div className="form-group">
        <label>Last Name</label>
        <input
          name="lastName"
          value={userData.lastName}
          onChange={handleChange}
          onBlur={handleBlurTrim}
          autoComplete="family-name"
        />
      </div>

      <div className="form-group">
        <label>Email</label>
        <input
          type="email"
          name="email"
          value={userData.email}
          onChange={handleChange}
          onBlur={handleBlurTrim}
          autoComplete="email"
        />
      </div>

      <div className="form-group">
        <label>Password</label>
        <div className="input-with-eye">
          <input
            type={showPwd ? "text" : "password"}
            name="password"
            value={userData.password}
            onChange={handleChange}
            onBlur={handleBlurTrim}
            onKeyUp={handleKey}
            autoComplete="new-password"
          />
          <span {...spanToggleProps(showPwd, setShowPwd, "Toggle password visibility")} />
        </div>

        {userData.password && (
          <>
            <div className="meter">
              {[1, 2, 3, 4, 5].map((i) => (
                <span
                  key={i}
                  className={`bar ${i <= passwordStrength ? "on" : ""}`}
                  style={{ background: i <= passwordStrength ? strengthMeta.color : "#e8e8e8" }}
                />
              ))}
              <span className="meter-label" style={{ color: strengthMeta.color }}>
                {strengthMeta.label} ({passwordStrength}/5)
              </span>
            </div>
            {capsLockOn && <div className="hint warning">Caps Lock is ON</div>}
            {passwordError && <div className="error-text">{passwordError}</div>}
          </>
        )}
      </div>

      <div className="form-group">
        <label>Confirm Password</label>
        <div className="input-with-eye">
          <input
            type={showConfirm ? "text" : "password"}
            name="confirmPassword"
            value={userData.confirmPassword}
            onChange={handleChange}
            onBlur={handleBlurTrim}
            onKeyUp={handleKey}
            autoComplete="new-password"
            onPaste={(e) => e.preventDefault()} // optional
          />
          <span {...spanToggleProps(showConfirm, setShowConfirm, "Toggle confirm password visibility")} />
        </div>
        {confirmError && <div className="error-text">{confirmError}</div>}
      </div>

      <style>{`
        .form-group { margin-bottom: 16px; }
        .input-with-eye {
          position: relative;
        }
        .input-with-eye input {
          width: 100%;
          padding-right: 44px; /* space for eye */
        }
        .eye-toggle {
          position: absolute;
          right: 10px;
          top: 50%;
          transform: translateY(-50%);
          cursor: pointer;
          user-select: none;
          font-size: 18px;
          line-height: 1;
          color: #555;
        }
        .eye-toggle:focus {
          outline: 2px solid #1677ff;
          outline-offset: 2px;
          border-radius: 4px;
        }

        .meter { display: flex; align-items: center; gap: 6px; margin-top: 8px; }
        .bar { width: 36px; height: 6px; border-radius: 3px; display: inline-block; }
        .meter-label { margin-left: 8px; font-weight: 600; }
        .error-text { color: #ff4d4f; margin-top: 6px; }
        .hint.warning { color: #faad14; margin-top: 6px; }
      `}</style>
    </div>
  );
}
